import { imageCompresser } from '@/commonFunctions/utils'
import { Delete, Image, PhotoCamera, Repeat } from '@/components/icons'
import { IMAGE_MAX_SIZE } from '@/constants/Common'
import { IMAGE_SIZE, IMAGE_TYPE } from '@/constants/MessageConstants'
import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { useOnClickOutside } from 'usehooks-ts'
import { ConfirmModal, UploadImageModal } from '../modal'

export type LogoInputProps = {
  label?: string
  labelClassName?: string
  error?: string
  defaultSrc?: string | File | null
  onSave?: (logo: any) => void
  onRemoveProfileLogo?: () => void
  uploadImageModalTitle: string
}

export const LogoInput = ({
  label,
  labelClassName,
  error,
  defaultSrc,
  onSave,
  onRemoveProfileLogo,
  uploadImageModalTitle,
  ...props
}: LogoInputProps) => {
  const [imageURL, setImageURL] = useState<string | null | undefined>(null)
  const [isActionsOpening, setIsActionsOpening] = useState<boolean>(false)
  const [isUploadImageOpening, setIsUploadImageOpening] = useState<boolean>(false)
  const [isRemoveLogoOpening, setIsRemoveLogoOpening] = useState<boolean>(false)
  const [imageName, setImageName] = useState<string | null>(null)
  const [modalError, setModalError] = useState<string | null>(null)
  const [image, setImage] = useState<string | ArrayBuffer | null>(null)

  useEffect(() => {
    if (defaultSrc && defaultSrc instanceof File) {
      setImageURL(URL.createObjectURL(defaultSrc))
    } else {
      setImageURL(defaultSrc)
    }
  }, [defaultSrc])

  const ref = useRef<HTMLInputElement>(null)
  const componentRef = useRef(null)

  useOnClickOutside(componentRef, () => {
    setIsActionsOpening(false)
  })

  const openUploadImageModal = () => {
    setIsUploadImageOpening(true)
  }

  const closeUploadImageModal = () => {
    setIsUploadImageOpening(false)
    !imageURL && setImage(null)

    if (ref.current && ref.current.value) {
      ref.current.value = ''
    }
  }

  const onRemoveLogo = () => {
    setIsRemoveLogoOpening(true)
  }

  const openSelectFile = () => {
    ref && ref.current && ref.current.click()
  }

  const onChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImageName(e.target.files[0].name)
      setModalError('')

      if (['image/png', 'image/jpeg'].includes(e.target.files[0].type)) {
        if (e.target.files[0].size >= IMAGE_MAX_SIZE) {
          setModalError(IMAGE_SIZE)
        } else {
          const compressedFile = await imageCompresser(e.target.files[0])
          const reader = new FileReader()
          reader.onload = () => {
            setImage(reader.result)
          }
          reader.readAsDataURL(compressedFile)
        }
      } else {
        setModalError(IMAGE_TYPE)
      }
    }
  }

  return (
    <>
      <div className="flex flex-col gap-y-2">
        {label && <p className={`text-sm text-white font-semibold ${labelClassName ? labelClassName : ''}`}>{label}</p>}
        <div className="w-32 h-32 relative" ref={componentRef}>
          <div
            className={`w-full h-full ${
              imageURL ? 'overflow-hidden rounded-full' : 'rounded-lg'
            } bg-white bg-opacity-90`}
          >
            {imageURL && <img className="w-32 h-32" src={imageURL} alt="Avatar" />}
            {!imageURL && (
              <button
                type="button"
                className="w-full h-full rounded-lg flex items-center border border-navy-900 justify-center border-opacity-20"
                onClick={openUploadImageModal}
              >
                {<Image className="text-navy-900 text-opacity-80 w-10 h-10" />}
              </button>
            )}
          </div>
          <input className="hidden" onChange={onChange} ref={ref} type="file" accept=".jpg, .jpeg, .png" {...props} />
          {imageURL && (
            <div className="w-10 h-10 absolute -bottom-1 -right-1">
              <button type="button" className="" onClick={() => setIsActionsOpening(!isActionsOpening)}>
                <PhotoCamera />
                {isActionsOpening && (
                  <ul className="bg-white z-20 overflow-hidden w-max shadow-lg rounded-md mt-1">
                    <li
                      className="flex flex-row gap-x-2 items-center py-3 px-4 hover:bg-slate-200"
                      onClick={() => {
                        openUploadImageModal()
                      }}
                    >
                      <Repeat className="text-azul" />
                      Change logo
                    </li>
                    <li
                      className="flex flex-row gap-x-2 items-center py-3 px-4 hover:bg-slate-200"
                      onClick={() => {
                        onRemoveLogo()
                      }}
                    >
                      <Delete className="text-boldOrange" />
                      Remove logo
                    </li>
                  </ul>
                )}
              </button>
            </div>
          )}
        </div>
        {error && <div className="text-boldOrange pt-1 font-medium">{error}</div>}
      </div>
      <UploadImageModal
        image={image}
        setImage={setImage}
        title={uploadImageModalTitle}
        open={isUploadImageOpening}
        onClose={closeUploadImageModal}
        handleLogo={(logo: File) => {
          onSave && onSave(logo)
          setImageURL(URL.createObjectURL(logo))
          setImageName(logo.name)
        }}
        openSelectFile={openSelectFile}
        {...props}
        imageName={imageName}
        error={modalError}
        defaultSrc={imageURL}
      />
      <ConfirmModal
        open={isRemoveLogoOpening}
        onClose={() => setIsRemoveLogoOpening(false)}
        onSubmit={() => {
          onRemoveProfileLogo && onRemoveProfileLogo()
          setImageURL(null)
          setImage(null)
          setIsRemoveLogoOpening(false)
          if (ref.current && ref.current.value) {
            ref.current.value = ''
          }
        }}
        title="Remove Logo"
        desc="Are you sure that you would like to remove logo?"
      />
    </>
  )
}

export default LogoInput
